import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { downloadFile, triggerAttachmentDownload } from '@/utils/fileDownload';
import { showErrorNotification } from '@/utils/notifications';
import { resolveFilePreviewKind, type FilePreviewKind } from '@/utils/filePreviewKind';
import { isIosSafariBrowser } from '@/utils/platform';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  filename: string;
  contentType?: string | null;
  kind?: FilePreviewKind;
  resolveDownloadUrl?: () => Promise<string>;
}

export function FilePreviewModal({
  isOpen,
  onClose,
  url,
  filename,
  contentType,
  kind,
  resolveDownloadUrl,
}: FilePreviewModalProps) {
  const { t } = useTranslation('filePreview');
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isMediaLoading, setIsMediaLoading] = useState(true);

  useEffect(
    function resetStateOnOpen() {
      if (isOpen === true) {
        setHasLoadError(false);
        setIsMediaLoading(true);
      }
    },
    [isOpen, url]
  );

  useEffect(
    function manageFocusAndBodyScroll() {
      if (isOpen === false) {
        return;
      }
      previouslyFocusedElementRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      if (closeButtonRef.current !== null) {
        closeButtonRef.current.focus();
      }
      return function restoreFocusAndBodyScroll() {
        document.body.style.overflow = previousBodyOverflow;
        if (previouslyFocusedElementRef.current !== null) {
          previouslyFocusedElementRef.current.focus();
        }
      };
    },
    [isOpen]
  );

  useEffect(
    function closeOnEscapeKey() {
      if (isOpen === false) {
        return;
      }
      function handleKeyDown(keyboardEvent: KeyboardEvent) {
        if (keyboardEvent.key === 'Escape') {
          onClose();
          return;
        }
        if (keyboardEvent.key === 'Tab') {
          trapFocusWithinDialog(dialogRef.current, keyboardEvent);
        }
      }
      window.addEventListener('keydown', handleKeyDown);
      return function removeKeyDownListener() {
        window.removeEventListener('keydown', handleKeyDown);
      };
    },
    [isOpen, onClose]
  );

  if (isOpen === false) {
    return null;
  }

  async function handleDownload() {
    try {
      if (resolveDownloadUrl === undefined) {
        await downloadFile(url, filename);
        return;
      }
      const attachmentUrl = await resolveDownloadUrl();
      triggerAttachmentDownload(attachmentUrl);
    } catch {
      showErrorNotification(t('downloadError'));
    }
  }

  const resolvedContentType = contentType === undefined ? null : contentType;
  const resolvedKind: FilePreviewKind =
    kind === undefined ? resolveFilePreviewKind(resolvedContentType, filename) : kind;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={filename}
      className="fixed inset-0 z-[100] bg-zooindigo bg-opacity-80 flex flex-col"
      onMouseDown={function closeWhenBackdropClicked(mouseEvent) {
        if (mouseEvent.target === mouseEvent.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-zooindigo bg-opacity-40 shrink-0">
        <p className="text-sm font-semibold text-zoowhite truncate min-w-0">{filename}</p>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="chip"
            onClick={function download() {
              void handleDownload();
            }}
          >
            <Icon name="Download" />
            {t('download')}
          </Button>
          <Button
            ref={closeButtonRef}
            variant="outline"
            size="icon"
            aria-label={t('close')}
            onClick={onClose}
          >
            <Icon name="X" />
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center p-4">
        <FilePreviewBody
          kind={resolvedKind}
          url={url}
          filename={filename}
          hasLoadError={hasLoadError}
          isMediaLoading={isMediaLoading}
          onMediaLoaded={function loaded() {
            setIsMediaLoading(false);
          }}
          onMediaError={function failed() {
            setIsMediaLoading(false);
            setHasLoadError(true);
          }}
          onDownload={function download() {
            void handleDownload();
          }}
        />
      </div>
    </div>
  );
}

function trapFocusWithinDialog(dialog: HTMLDivElement | null, keyboardEvent: KeyboardEvent) {
  if (dialog === null) {
    return;
  }
  const focusableElements = dialog.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  if (firstElement === undefined || lastElement === undefined) {
    keyboardEvent.preventDefault();
    return;
  }
  const activeElement = document.activeElement;
  if (keyboardEvent.shiftKey === true && activeElement === firstElement) {
    keyboardEvent.preventDefault();
    lastElement.focus();
    return;
  }
  if (keyboardEvent.shiftKey === false && activeElement === lastElement) {
    keyboardEvent.preventDefault();
    firstElement.focus();
  }
}

interface FilePreviewBodyProps {
  kind: FilePreviewKind;
  url: string;
  filename: string;
  hasLoadError: boolean;
  isMediaLoading: boolean;
  onMediaLoaded: () => void;
  onMediaError: () => void;
  onDownload: () => void;
}

function FilePreviewBody({
  kind,
  url,
  filename,
  hasLoadError,
  isMediaLoading,
  onMediaLoaded,
  onMediaError,
  onDownload,
}: FilePreviewBodyProps) {
  const { t } = useTranslation('filePreview');

  if (hasLoadError) {
    return (
      <div className="flex flex-col items-center gap-4 text-zoowhite">
        <p className="text-sm">{t('loadError')}</p>
        <Button variant="outline" size="chip" onClick={onDownload}>
          <Icon name="Download" />
          {t('download')}
        </Button>
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        {isMediaLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner className="w-8 h-8" />
            <span className="sr-only">{t('loading')}</span>
          </div>
        ) : null}
        <img
          src={url}
          alt=""
          className="max-h-full max-w-full object-contain"
          onLoad={onMediaLoaded}
          onError={onMediaError}
        />
      </div>
    );
  }

  if (kind === 'pdf') {
    if (isIosSafariBrowser(navigator.userAgent)) {
      return (
        <div className="flex flex-col items-center gap-4 text-zoowhite">
          <Icon name="FileText" className="text-4xl" />
          <p className="text-sm text-center">{t('pdfEmbedUnsupported')}</p>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-zoowhite border-opacity-40 text-sm font-medium hover:bg-zoowhite hover:bg-opacity-10"
          >
            <Icon name="Link" />
            {t('openInBrowser')}
          </a>
        </div>
      );
    }
    return <iframe src={url} title={filename} className="w-full h-full bg-zoowhite rounded-lg" />;
  }

  if (kind === 'video') {
    return (
      <video
        src={url}
        controls
        className="max-h-full max-w-full"
        onLoadedData={onMediaLoaded}
        onError={onMediaError}
      />
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 text-zoowhite">
      <Icon name="FileText" className="text-4xl" />
      <p className="text-sm">{t('unsupported')}</p>
      <Button variant="outline" size="chip" onClick={onDownload}>
        <Icon name="Download" />
        {t('download')}
      </Button>
    </div>
  );
}
